import React from 'react';
import { ApolloError } from '@apollo/client';
import { getApplicationError, getValidationErrors } from '@oysterjs/core/graphql/errors';

import { client } from '~/api/client';
import {
  AddCaraWorkspaceMessageDocument,
  type AddCaraWorkspaceMessageMutation,
  type CaraWorkspaceFieldsFragment,
  type CaraWorkspaceMessageFollowUpInput
} from '~/api/graphql';
import {
  type CopilotActionError,
  copilotActionErrorFromApplicationError
} from '~/lib/copilot-action-error';
import {
  type CaraAttachment,
  amsContextFromAttachments,
  amsRefsFromAttachments,
  filesFromAttachments,
  idsByKind
} from '~/components/cara-input/cara-attachment';
import { buildCaraFallbackMessage } from '~/components/workspaces/lib/utils';

/**
 * Owns the send-message mutation for a workspace. Attachments — files and
 * library picks alike — are passed in by the caller as a single
 * `CaraAttachment[]`; this hook fans them out into the right fields on the
 * mutation input. A ref-based reentrancy guard prevents duplicate sends
 * from rapid double-clicks.
 *
 * The returned `addCaraWorkspaceMessage` is wrapped in `useCallback` so it
 * can be safely passed to downstream hooks/effects.
 */
export function useAddCaraWorkspaceMessage(args: {
  workspace: CaraWorkspaceFieldsFragment | undefined;
  setWorkspace: (workspace: CaraWorkspaceFieldsFragment) => void;
}) {
  const { workspace, setWorkspace } = args;

  const [addMessageError, setAddMessageError] = React.useState<CopilotActionError>();

  // Use a ref to prevent race conditions when sending messages rapidly (e.g.,
  // double-clicking send). Unlike useState, ref updates are synchronous - when we set
  // addMessageLoadingRef.current = true, the value is immediately available on the next
  // line. With useState, setLoading(true) schedules an update for the next render, so
  // multiple rapid calls would all see loading=false and execute concurrently,
  // potentially sending duplicate messages.
  const addMessageLoadingRef = React.useRef(false);

  const workspaceId = workspace?.id;

  const clearAddMessageError = React.useCallback(() => {
    setAddMessageError(undefined);
  }, []);

  const addCaraWorkspaceMessage = React.useCallback(
    async (
      query: string,
      attachments: CaraAttachment[],
      followUp?: CaraWorkspaceMessageFollowUpInput
    ) => {
      if (!workspaceId) return;
      if (addMessageLoadingRef.current) return;

      addMessageLoadingRef.current = true;

      const customFormIds = idsByKind(attachments, 'custom-form');
      const htmlTemplateIds = idsByKind(attachments, 'html-template');
      const wordTemplateIds = idsByKind(attachments, 'word-template');
      const excelTemplateIds = idsByKind(attachments, 'excel-template');
      const files = filesFromAttachments(attachments);
      const amsContext = amsContextFromAttachments(attachments);
      const amsRefs = amsRefsFromAttachments(attachments);

      const messageText =
        query.trim() ||
        buildCaraFallbackMessage({
          customFormIds,
          htmlTemplateIds,
          wordTemplateIds,
          excelTemplateIds
        });

      try {
        const res = await client.mutate<AddCaraWorkspaceMessageMutation>({
          mutation: AddCaraWorkspaceMessageDocument,
          variables: {
            id: workspaceId,
            input: {
              message: messageText,
              files,
              ...(customFormIds.length ? { customFormIds } : {}),
              ...(htmlTemplateIds.length ? { htmlDocumentTemplateIds: htmlTemplateIds } : {}),
              ...(wordTemplateIds.length ? { wordDocumentTemplateIds: wordTemplateIds } : {}),
              ...(excelTemplateIds.length ? { excelSpreadsheetTemplateIds: excelTemplateIds } : {}),
              ...(amsContext.length ? { amsContext } : {}),
              ...(amsRefs.length ? { amsRefs } : {}),
              ...(followUp ? { followUp } : {})
            }
          }
        });

        const applicationError = getApplicationError(
          new ApolloError({ graphQLErrors: res.errors })
        );
        const validationErrors = getValidationErrors(
          new ApolloError({ graphQLErrors: res.errors })
        );

        if (applicationError) {
          const actionError = copilotActionErrorFromApplicationError(applicationError);
          setAddMessageError(actionError);
          // Throw so CaraInput does not treat this as success (it clears draft /
          // attachments on resolve, which would immediately wipe this error via
          // onAttachmentsChanged).
          throw new Error(actionError.message);
        }
        if (validationErrors?.length) {
          const actionError = {
            kind: 'generic' as const,
            message: validationErrors[0].message
          };
          setAddMessageError(actionError);
          throw new Error(actionError.message);
        }
        if (res.errors?.length) {
          const actionError = {
            kind: 'generic' as const,
            message: res.errors[0].message
          };
          setAddMessageError(actionError);
          throw new Error(actionError.message);
        }
        if (res.data?.addCaraWorkspaceMessage) {
          setAddMessageError(undefined);
          setWorkspace(res.data.addCaraWorkspaceMessage);
        } else {
          throw new Error('Failed to add message to workspace');
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        // Re-throw so callers (CaraInput) skip the success-path draft/attachment clear.
        throw e;
      } finally {
        addMessageLoadingRef.current = false;
      }
    },
    [workspaceId, setWorkspace]
  );

  return {
    addMessageError,
    clearAddMessageError,
    addCaraWorkspaceMessage
  };
}
